import { Injectable } from '@angular/core';
import * as Parse from 'parse';

export interface IOrganization {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    businessName: string;
    businessPhone: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    website: string;
    primaryColor: string;
    secondaryColor: string;
}

export interface IOrganizationForm {
    name: string;
    phone: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    website: string;
}

export type VestaPlan = 'Analysis' | 'Contracts' | 'Marketing' | 'Enterprise' | 'PI';
export type VestaFeature = 'flyer'
    | 'customer-token'
    | 'customer-signup'
    | 'contract-confirm'
    | 'confirm-customer'
    | 'update-preferred-agent'
    | 'unsubscribe'
    | 'hotsheet'
    | 'customers'
    | 'subscriptions'
    | 'contracts'
    | 'file-library'
    | 'vendors'
    | 'property-history'
    | 'property-offer-calculator'
    | 'property-rehab-calculator'
    | 'property-aa-flip'
    | 'property-aa-rental'
    | 'property-flip-analysis'
    | 'property-rental-analysis'
    | 'property-attachments'
    | 'property-marketing'
    | 'property-contracts'
    | 'property-vendors';

@Injectable()
export class OrganizationService {
    #organization: Parse.Object | undefined;
    #rootOrganizationId: string = '8zwlokv2y5';

    get organization() {
        return this.#organization as Parse.Object;
    }

    set organization(org: Parse.Object) {
        this.#organization = org;
    }

    get rootOrganizationId() {
        return this.#rootOrganizationId;
    }

    get isPI(): boolean {
        return this.organization?.get('plan') === 'PI';
    }

    get isAnalysis(): boolean {
        return this.organization?.get('plan') === 'Analysis';
    }

    get isContracts(): boolean {
        return this.organization?.get('plan') === 'Contracts';
    }

    get isMarketing(): boolean {
        return this.organization?.get('plan') === 'Marketing';
    }

    get isEnterprise(): boolean {
        return this.organization?.get('plan') === 'Enterprise';
    }

    constructor() {
    }

    canActivate(feature: VestaFeature): boolean {
        switch (feature) {
            // organization level routes
            case 'flyer':
            case 'customer-token':
            case 'customer-signup':
            case 'confirm-customer':
            case 'update-preferred-agent':
            case 'unsubscribe':
                return (this.isMarketing || this.isEnterprise);
            case 'contract-confirm':
                return (this.isContracts || this.isEnterprise);

            // dashboard routes
            case 'hotsheet':
            case 'file-library':
                return this.isEnterprise;
            case 'customers':
                return this.isMarketing || this.isEnterprise;
            case 'subscriptions':
                return this.organization.id === this.#rootOrganizationId;
            case 'contracts':
                return this.isContracts || this.isEnterprise;
            case 'vendors':
                return this.isPI;

            // property routes
            case 'property-history':
                return this.isAnalysis || this.isContracts || this.isMarketing || this.isEnterprise;
            case 'property-offer-calculator':
            case 'property-rehab-calculator':
            case 'property-aa-flip':
            case 'property-aa-rental':
            case 'property-flip-analysis':
            case 'property-rental-analysis':
                return this.isAnalysis || this.isEnterprise;
            case 'property-attachments':
                return this.isAnalysis || this.isContracts || this.isMarketing || this.isEnterprise;
            case 'property-marketing':
                return this.isMarketing || this.isEnterprise;
            case 'property-contracts':
                return this.isContracts || this.isEnterprise;
            case 'property-vendors':
                return this.isPI;

            default:
                return false;
        }
    }

    createOrganization(organization: IOrganization) {
        return Parse.Cloud.run('createOrganization', {
            organization: {
                firstname: organization.firstname.trim(),
                lastname: organization.lastname.trim(),
                email: organization.email.trim(),
                phone: organization.phone.trim().replace(/\D+/g, ''),
                businessName: organization.businessName.trim().toLowerCase(),
                businessPhone: organization.businessPhone.trim().replace(/\D+/g, ''),
                address: organization.address.trim().toLowerCase(),
                address2: organization.address2.trim().toLowerCase(),
                city: organization.city.trim().toLowerCase(),
                state: organization.state.trim().toLowerCase(),
                zip: organization.zip.trim().toLowerCase(),
                website: organization.website.trim().toLowerCase(),
                primaryColor: organization.primaryColor.trim(),
                secondaryColor: organization.secondaryColor.trim()
            }
        });
    }

    updateOrganization(formData: IOrganizationForm) {
        return Parse.Cloud.run('updateOrganization', {
            organizationId: this.organization.id,
            payload: {
                name: formData.name.trim().toLowerCase(),
                phone: formData.phone.trim().replace(/\D+/g, ''),
                address: formData.address.trim().toLowerCase(),
                address2: formData.address2.trim().toLowerCase(),
                city: formData.city.trim().toLowerCase(),
                state: formData.state.trim().toLowerCase(),
                zip: formData.zip.trim().toLowerCase(),
                website: formData.website.trim().toLowerCase()
            }
        });
    }

    rollbackOrganization(userId: string) {
        return Parse.Cloud.run('rollbackOrganization', {
            userId: userId
        });
    }

    validateOrganization(id: string) {
        return Parse.Cloud.run('validateOrganization', {
            organizationId: id
        });
    }

    getOrganizations(pageIndex: number, pageSize: number) {
        return Parse.Cloud.run('getOrganizations', {
            pageIndex: pageIndex,
            pageSize: pageSize
        });
    }

    searchOrganizations(searchTerm: string, pageIndex: number, pageSize: number) {
        return Parse.Cloud.run('searchOrganizations', {
            searchTerm: searchTerm,
            pageIndex: pageIndex,
            pageSize: pageSize
        });
    }
}
